import React, { useState } from 'react';
import Button from '@app/react-common/components/button/Button';
import Dropdown, { Option } from '@app/react-common/components/dropdown/Dropdown';
import Input from '@app/react-common/components/input/Input';
import { DeliveryMethodSection, SECTION_NAME_MAX_LENGTH } from '../../sections/deliveryMethodDisplayOrder';

export type MoveToSectionTarget = { kind: 'existing'; sectionId: string | null } | { kind: 'new'; sectionName: string };

interface MoveToSectionViewProps {
  sections: DeliveryMethodSection[];
  onBack: () => void;
  onClose: () => void;
  onMoveToSection: (target: MoveToSectionTarget) => void;
}

const NEW_SECTION_OPTION_VALUE = 'new-section';
const NO_SECTION_OPTION_VALUE = 'no-section';

export const MoveToSectionView = ({ sections, onBack, onClose, onMoveToSection }: MoveToSectionViewProps) => {
  const [selectedTargetOption, setSelectedTargetOption] = useState<Option | null>(null);
  const [newSectionName, setNewSectionName] = useState('');

  const targetSectionOptions: Option[] = [
    ...sections.map((section) => ({ label: section.name, value: section.id })),
    { label: 'New section', value: NEW_SECTION_OPTION_VALUE },
    { label: 'No section', value: NO_SECTION_OPTION_VALUE },
  ];
  const isCreatingNewSection = selectedTargetOption?.value === NEW_SECTION_OPTION_VALUE;
  const canMove = !!selectedTargetOption && (!isCreatingNewSection || newSectionName.trim() !== '');

  const handleMove = () => {
    if (isCreatingNewSection) {
      onMoveToSection({ kind: 'new', sectionName: newSectionName.trim() });
      return;
    }
    onMoveToSection({
      kind: 'existing',
      sectionId: selectedTargetOption.value === NO_SECTION_OPTION_VALUE ? null : selectedTargetOption.value,
    });
  };

  return (
    <div className="bulk-edit-modal__view">
      <div className="bulk-edit-modal__header">
        <div className="bulk-edit-modal__header__left">
          <Button icon="back" variant="ghost" size="small" onClick={onBack} />
          <h2>Move to section</h2>
        </div>
        <Button icon="close" variant="ghost" size="small" onClick={onClose} />
      </div>
      <div className="bulk-edit-modal__content">
        <div className="bulk-edit-modal__field">
          <label className="bulk-edit-modal__label">Target section</label>
          <Dropdown
            options={targetSectionOptions}
            placeholder="Select section"
            preSelectedOption={selectedTargetOption}
            onSelect={setSelectedTargetOption}
          />
        </div>
        {isCreatingNewSection && (
          <div className="bulk-edit-modal__field">
            <label className="bulk-edit-modal__label">New section name</label>
            <Input
              type="text"
              value={newSectionName}
              onChange={(event) => setNewSectionName(event.target.value)}
              placeholder="Section name"
              maxLength={SECTION_NAME_MAX_LENGTH}
            />
          </div>
        )}
      </div>
      <div className="bulk-edit-modal__footer">
        <Button text="Cancel" onClick={onClose} size="medium" variant="secondary" />
        <Button text="Move" onClick={handleMove} size="medium" variant="primary" disabled={!canMove} />
      </div>
    </div>
  );
};
